import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { AlertCircle } from 'lucide-react';
import { maskAddressForMap } from '@/lib/addressMasking';

// Security: Escape HTML to prevent XSS attacks
const escapeHtml = (text: string | null | undefined): string => {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

interface MapProps {
  listings: Array<{
    id: string;
    title: string;
    address: string;
    geo_lat: number | null;
    geo_lng: number | null;
    base_price_day: number | null;
  }>;
  center?: [number, number];
  zoom?: number;
  height?: string;
}

const Map = ({ listings, center = [10.0, 56.0], zoom = 6, height = '500px' }: MapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const [mapError, setMapError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Check for WebGL support
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) {
        setMapError('Dit browser understøtter ikke kort visning (WebGL er påkrævet)');
        return;
      }
    } catch (e) {
      setMapError('Kunne ikke initialisere kort visning');
      return;
    }

    try {
      mapboxgl.accessToken = 'pk.eyJ1IjoiYW5kcjI4M2UiLCJhIjoiY21nN3ZiNjgwMDBxdzJqcXVxZHF1emJ1NiJ9.tkHoEAkBxzLhf1hLvsXvpQ';

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center,
        zoom,
        failIfMajorPerformanceCaveat: false, // Allow map on devices with limited WebGL support
      });

      map.current.on('error', (e) => {
        console.error('Mapbox error:', e);
        setMapError('Kortet kunne ikke indlæses korrekt');
      });

      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    } catch (error) {
      console.error('Failed to initialize map:', error);
      setMapError('Kortet kunne ikke startes');
      return;
    }

    return () => {
      try {
        markers.current.forEach(m => m.remove());
        markers.current = [];
        if (map.current) {
          map.current.remove();
          map.current = null;
        }
      } catch (e) {
        console.error('Error cleaning up map:', e);
      }
    };
  }, [center, zoom]);

  useEffect(() => {
    if (!map.current) return;

    const addMarkers = () => {
      // Fjern gamle markers
      markers.current.forEach(m => m.remove());
      markers.current = [];

      // Tilføj nye markers
      const validListings = listings.filter(l => l.geo_lat && l.geo_lng);

      validListings.forEach((listing) => {
        const el = document.createElement('div');
        el.className = 'marker';
        el.style.width = '32px';
        el.style.height = '32px';
        el.style.borderRadius = '50%';
        el.style.background = 'hsl(var(--primary))';
        el.style.border = '2px solid white';
        el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
        el.style.cursor = 'pointer';
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        el.style.justifyContent = 'center';
        el.style.color = 'white';
        el.style.fontSize = '12px';
        el.style.fontWeight = 'bold';
        el.innerHTML = 'P';

        // Security: Escape user-provided data to prevent XSS
        const safeTitle = escapeHtml(listing.title);
        const maskedAddress = maskAddressForMap(listing.address);
        const safeAddress = escapeHtml(maskedAddress);
        const safePrice = listing.base_price_day ? Math.round(listing.base_price_day / 100) : '—';
        
        const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
          <div style="padding: 12px; min-width: 200px;">
            <h3 style="margin: 0 0 6px 0; font-weight: 600; font-size: 14px;">${safeTitle}</h3>
            <p style="margin: 0 0 8px 0; font-size: 12px; color: #666;">${safeAddress}</p>
            <p style="margin: 0 0 12px 0; font-weight: 600; color: hsl(var(--primary));">
              ${safePrice} kr/dag
            </p>
            <button 
              data-listing-id="${listing.id}"
              style="width: 100%; padding: 8px 16px; background: hsl(var(--primary)); color: white; border: none; font-size: 13px; font-weight: 500; border-radius: 6px; cursor: pointer; transition: opacity 0.2s;"
              onmouseover="this.style.opacity='0.9'"
              onmouseout="this.style.opacity='1'"
            >
              Se detaljer
            </button>
          </div>
        `);

        const marker = new mapboxgl.Marker(el)
          .setLngLat([listing.geo_lng!, listing.geo_lat!])
          .setPopup(popup)
          .addTo(map.current!);

        // Add click handler to the button inside the popup
        popup.on('open', () => {
          const popupElement = popup.getElement();
          const button = popupElement?.querySelector(`button[data-listing-id="${listing.id}"]`);
          if (button) {
            button.addEventListener('click', () => {
              navigate(`/spot/${listing.id}`);
            });
          }
        });

        markers.current.push(marker);
      });

      // Fit bounds hvis der er listings
      if (validListings.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        validListings.forEach(l => {
          bounds.extend([l.geo_lng!, l.geo_lat!]);
        });
        map.current!.fitBounds(bounds, { padding: 50, maxZoom: 12 });
      }
    };

    if (map.current.loaded()) {
      addMarkers();
    } else {
      map.current.on('load', addMarkers);
    }
  }, [listings]);

  if (mapError) {
    return (
      <div 
        style={{ width: '100%', height }}
        className="rounded-lg shadow-lg overflow-hidden bg-muted flex items-center justify-center"
      >
        <div className="text-center p-6 max-w-md">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">{mapError}</p>
          {listings.length > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              Der er {listings.length} parkering{listings.length > 1 ? 'spladser' : 'splads'} tilgængelige på listen
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={mapContainer} 
      style={{ width: '100%', height }}
      className="rounded-lg shadow-lg overflow-hidden"
    />
  );
};

export default Map;
